'use client'

import type { CSSProperties } from 'react'
import type {
	SRT_Column,
	SRT_Row,
	SRT_RowData,
	SRT_TableInstance,
} from '../../types'

import React, { useMemo } from 'react'

import {
	Table,
	TableCell,
	TableFooter,
	TableRow,
} from '@/shared/components/ui/table'

import { useGridVirtualizer } from '../../hooks/useGridVirtualizer'
import { cn } from '../../lib/utils'
import {
	getHeaderPinningStyles,
	getHeaders,
	parseFromValuesOrFunc,
} from '../../utils/utils'
import { SRT_TableBody } from '../body/SRT_TableBody'
import { SRT_TableHead } from '../head/SRT_TableHead'

function defaultGetRowHeight<T extends SRT_RowData>(row?: SRT_Row<T>) {
	if (row?.groupingColumnId) {
		return 80
	}

	return 36
}
export function MasterTable<T extends SRT_RowData>({
	table,
}: {
	table: SRT_TableInstance<T>
}) {
	const {
		options: {
			divideX,
			divideY,
			layoutMode,
			enableVirtualization,
			enableTableFooter,
			enableTableHead,
			renderGroup,
			renderDetailPanel,
			getRowHeight = defaultGetRowHeight,
			defaultRowHeight,
		},
		refs: { tableContainerRef },
	} = table

	const grid = useGridVirtualizer(table, {
		getRowHeight,
		getColSize: (column: SRT_Column<T>) => {
			const colSize = column?.getSize()

			return colSize === Number.MAX_SAFE_INTEGER ? 200 : colSize
		},
	})
	const totalSize = table.getTotalSize()
	const headers = getHeaders(table)

	const columns = table.getVisibleLeafColumns()
	const {
		columnSizing,
		columnSizingInfo,
		columnVisibility,
		rowPinning,
		columnPinning,
	} = table.getState()

	const columnSizes = useMemo(() => {
		const headers = table
			.getFlatHeaders()
			.filter(({ id, column }) => id == column.id)

		const colSizes: {
			id: string
			size: number | undefined
		}[] = []
		for (let i = 0; i < headers.length; i++) {
			const header = headers[i]
			const colSize = header.getSize()
			colSizes.push({
				id: header.column.id,
				size: colSize === Number.MAX_SAFE_INTEGER ? undefined : colSize,
			})
		}

		return colSizes
	}, [
		columns,
		columnSizing,
		columnSizingInfo,
		columnVisibility,
		rowPinning,
		columnPinning,
	])

	const rowSizeVars = useMemo(
		() => ({
			['--data-row-height']: defaultRowHeight + 'px',
		}),
		[defaultRowHeight]
	)

	return (
		<div
			className='relative flex min-h-0 flex-1 flex-col !overflow-auto text-sm'
			ref={tableContainerRef}
			style={{ ...rowSizeVars } as CSSProperties}
		>
			{enableTableHead && (
				<Table
					className='sticky top-0 z-2 table-fixed border-separate bg-background'
					style={layoutMode == 'semantic' ? { width: totalSize } : undefined}
				>
					<colgroup>
						{columnSizes.map(({ id, size }) => (
							<col key={id} style={size ? { width: size } : undefined} />
						))}
					</colgroup>
					<SRT_TableHead table={table} />
				</Table>
			)}

			<div
				className='flex flex-1 flex-col'
				style={layoutMode == 'semantic' ? { width: totalSize } : undefined}
			>
				<Table className='table-fixed border-separate'>
					<colgroup>
						{columnSizes.map(({ id, size }) => (
							<col key={id} style={size ? { width: size } : undefined} />
						))}
					</colgroup>

					<SRT_TableBody
						table={table}
						layoutMode={layoutMode}
						grid={enableVirtualization ? grid?.bodyGrid : undefined}
						renderGroup={renderGroup}
						renderDetailPanel={renderDetailPanel}
					/>
				</Table>
			</div>

			{enableTableFooter && (
				<Table
					className='sticky bottom-0 z-1 border-separate bg-background'
					style={layoutMode == 'semantic' ? { width: totalSize } : undefined}
				>
					<TableFooter>
						{headers.map((headers, i) => (
							<TableRow key={i}>
								{headers.map(({ header, isPinned, left, right }) => (
									<TableCell
										colSpan={header.colSpan}
										key={`${header.id}`}
										className={cn('h-9 bg-inherit px-2', {
											'border-r': divideX,
											'border-t': divideY,
										})}
										style={getHeaderPinningStyles(header, {
											isPinned,
											left,
											right,
											layoutMode,
										})}
									>
										<div className='whitespace-nowrap'>
											{header.isPlaceholder ? null : (
												<div className='flex items-center justify-between text-xs'>
													{header.isPlaceholder
														? null
														: (parseFromValuesOrFunc(
																header.column.columnDef.Footer,
																{
																	column: header.column,
																	footer: header.column.columnDef.footer as any,
																	table,
																}
															) ??
															header.column.columnDef.footer ??
															null)}
												</div>
											)}
										</div>
									</TableCell>
								))}
							</TableRow>
						))}
					</TableFooter>
				</Table>
			)}
		</div>
	)
}
